import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FiMail, FiLock, FiAlertCircle, FiShield } from 'react-icons/fi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent} from '../components/ui/Card';
import Logo from '../components/ui/Logo';

const LoginPage = () => {
  const { 
    login, 
    isAuthenticated, 
    isLoading, 
    error, 
    loginAttempts, 
    isLocked, 
    lockedUntil,
    clearError 
  } = useAdminAuth();
  
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [lockoutInfo, setLockoutInfo] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // Handle redirect after authentication
  useEffect(() => {
    if (isAuthenticated) {
      //console.log('🎯 Authentication successful, redirecting to dashboard...');
      window.location.href = from;
    }
  }, [isAuthenticated, from]);

  // Handle lockout countdown
  useEffect(() => {
    if (isLocked && lockedUntil) {
      const updateCountdown = () => {
        const now = new Date();
        const lockEnd = new Date(lockedUntil);
        const remaining = Math.max(0, lockEnd - now);
        
        if (remaining > 0) {
          setRemainingTime(remaining);
          setLockoutInfo({
            message: 'Account temporarily locked due to multiple failed attempts',
            remainingMinutes: Math.ceil(remaining / 60000)
          });
        } else {
          setLockoutInfo(null);
          setRemainingTime(0);
        }
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      
      return () => clearInterval(interval);
    } else {
      setLockoutInfo(null);
      setRemainingTime(0);
    }
  }, [isLocked, lockedUntil]);

  // Clear error when user starts typing
  useEffect(() => {
    const subscription = watch(() => {
      if (error) {
        clearError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, error, clearError]);

  const onSubmit = async (data) => {
    try {
      //console.log('🚀 Attempting login...');
      const result = await login(data.email, data.password);
      //console.log('✅ Login successful, result:', result);
      //console.log('🔄 Authentication state should update now...');
    } catch (err) {
      // Error is handled by the context and displayed via toast
      console.error('❌ Login error:', err);
    }
  };

  const formatRemainingTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto mb-6 flex justify-center">
            <Logo
              size="xlarge"
              className="h-16 w-auto"
              alt="CrowBlock Admin"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Admin Panel
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access the CROWBLOCK admin dashboard
          </p>
        </div>

        {/* Login Form */}
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Security Notice */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <FiShield className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                <div className="text-sm">
                  <p className="text-blue-800 font-medium">Security Notice</p>
                  <p className="text-blue-700 mt-1">
                    This area is restricted to authorized administrators only. 
                    All login attempts are monitored and logged.
                  </p>
                </div>
              </div>
            </div>

          {/* Lockout Warning */}
          {lockoutInfo && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                <div className="text-sm">
                  <p className="text-red-800 font-medium">Account Temporarily Locked</p>
                  <p className="text-red-700 mt-1">
                    {lockoutInfo.message}
                  </p>
                  {remainingTime > 0 && (
                    <p className="text-red-600 mt-2 font-mono">
                      Time remaining: {formatRemainingTime(remainingTime)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Attempts Warning */}
          {!isLocked && loginAttempts < 3 && loginAttempts > 0 && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <FiAlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                <div className="text-sm">
                  <p className="text-yellow-800 font-medium">Login Attempts Warning</p>
                  <p className="text-yellow-700 mt-1">
                    {loginAttempts} login {loginAttempts === 1 ? 'attempt' : 'attempts'} remaining 
                    before temporary lockout
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-l3-b text-neutral-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <FiMail className="absolute left-3 top-3 h-5 w-5 text-neutral-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  disabled={isLoading || isLocked}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={`pl-10 ${errors.email ? 'border-secondary-red' : ''}`}
                  placeholder="admin@crowblock.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-p3-r text-secondary-red">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-l3-b text-neutral-700 mb-2">
                Password
              </label>
              <div className="relative">
                <FiLock className="absolute left-3 top-3 h-5 w-5 text-neutral-400" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  disabled={isLoading || isLocked}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    }
                  })}
                  className={`pl-10 ${errors.password ? 'border-secondary-red' : ''}`}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-p3-r text-secondary-red">{errors.password.message}</p>
              )}
            </div>

            {/* Error Display */}
            {error && !isLocked && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-p3-r text-secondary-red">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading || isLocked}
              className="w-full"
              variant={isLoading || isLocked ? "secondary" : "default"}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : isLocked ? (
                'Account Locked'
              ) : (
                'Sign In to Admin Panel'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p>CROWBLOCK Admin Panel v1.0</p>
            <p className="mt-1">
              Unauthorized access is prohibited and monitored
            </p>
          </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
